import { useEffect, useState } from "react";
import { Radio, MoonStar, Sunrise, Sunset } from "lucide-react";
import { api } from "@/lib/api";

// Poll clock every 60s
const POLL_MS = 60_000;

function formatTimeShort(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    return d.toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit", timeZone: "America/New_York" });
  } catch {
    return "";
  }
}

const SESSION_MAP = {
  OPEN: {
    label: "Open",
    icon: Radio,
    cls: "border-emerald-500/50 bg-emerald-500/10 text-emerald-300",
    pulse: true,
  },
  PRE_MARKET: {
    label: "Pre-Market",
    icon: Sunrise,
    cls: "border-amber-500/50 bg-amber-500/10 text-amber-300",
    pulse: false,
  },
  AFTER_HOURS: {
    label: "After-Hours",
    icon: Sunset,
    cls: "border-amber-500/40 bg-amber-500/10 text-amber-300",
    pulse: false,
  },
  CLOSED: {
    label: "Closed",
    icon: MoonStar,
    cls: "border-slate-600 bg-slate-800/40 text-slate-300",
    pulse: false,
  },
  LOADING: {
    label: "Loading",
    icon: Radio,
    cls: "border-slate-700 bg-slate-800/40 text-slate-500",
    pulse: false,
  },
  UNKNOWN: {
    label: "Loading",
    icon: Radio,
    cls: "border-slate-700 bg-slate-800/40 text-slate-500",
    pulse: false,
  },
};

export default function MarketStatusChip() {
  const [status, setStatus] = useState(null);

  useEffect(() => {
    let cancelled = false;
    let tid;
    const fetchStatus = async () => {
      try {
        const { data } = await api.get("/intraday/clock");
        if (!cancelled) setStatus(data);
      } catch (_) {
        if (!cancelled) setStatus({ session: "UNKNOWN", is_open: false });
      }
    };
    fetchStatus();
    tid = setInterval(fetchStatus, POLL_MS);
    return () => { cancelled = true; clearInterval(tid); };
  }, []);

  const session = status ? (status.session || "UNKNOWN") : "LOADING";
  const meta = SESSION_MAP[session] || SESSION_MAP.UNKNOWN;
  const Icon = meta.icon;
  const isOpen = session === "OPEN";
  const nextTime = isOpen ? status?.next_close : status?.next_open;
  const nextLabel = isOpen ? "closes" : "opens";
  return (
    <div
      data-testid="market-status-chip"
      title={status?.server_time ? `Server: ${status.server_time}` : ""}
      className={`hidden sm:flex items-center gap-1.5 h-8 px-2.5 border mono text-[10px] uppercase tracking-widest ${meta.cls}`}
    >
      <span className="relative flex h-2 w-2 items-center justify-center">
        <Icon className="h-2.5 w-2.5" />
        {meta.pulse && (
          <span className="absolute inline-flex h-2 w-2 rounded-full bg-emerald-400 opacity-60 animate-ping" />
        )}
      </span>
      <span data-testid="market-status-label" className="font-bold">{meta.label}</span>
      {nextTime && (
        <>
          <span className="text-slate-500">·</span>
          <span className="text-slate-400">{nextLabel} {formatTimeShort(nextTime)} ET</span>
        </>
      )}
    </div>
  );
}
